import { IBreadcrumbBarItem } from "component-library";

import {
  prepareFollowUpTitle,
  prepareSent,
} from "shared/helpers/followUpViewHelpers";
import { IFacetGroup } from "shared/models/facet";
import { IFollowUp } from "shared/models/followUp";
import { navigator } from "shared/services/navigator";

import { PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IActionList, IActionListItem } from "models/common";
import {
  ICommonTranslation,
  IFollowUpPageTranslation,
} from "models/translation";

import defaultsWithFallback from "helpers/defaultsWithFallback";
import { getPageTitle } from "helpers/getPageTitle";
import { ListItemTranslator } from "helpers/translationHelpers";
import { fallbackTranslation } from "services/api/fallbackData";
import applicationMap from "services/applicationMap";

import { IFollowUpPageTexts } from "./FollowUpPage";
import { breadcrumbsKeys } from "pages/JobPage/test.helpers";

const fallbackPageTranslation = fallbackTranslation[PageKey.FollowUp];
const fallbackCommonTranslation = fallbackTranslation.common;

export enum ActionListItemKey {
  BackToJobsFeed = "BackToJobsFeed",
}

export function prepareBreadcrumbs(
  pageTranslation: IFollowUpPageTranslation,
  accountUrl: string
): IBreadcrumbBarItem[] {
  const listItemTranslator = new ListItemTranslator(
    pageTranslation?.header?.breadcrumb,
    fallbackPageTranslation.header.breadcrumb
  );

  const jobsLink = applicationMap.links.jobsMyFilters();

  return [
    {
      url: accountUrl,
      text: listItemTranslator.get(breadcrumbsKeys.myAccount),
    },
    {
      url: jobsLink,
      text: listItemTranslator.get(breadcrumbsKeys.jobsFeed),
      handleClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigator.goTo(jobsLink);
      },
    },
  ];
}

export function prepareTexts(
  pageTranslation: IFollowUpPageTranslation,
  commonTranslation: ICommonTranslation,
  followUp: IFollowUp,
  productionTypes: IFacetGroup[]
): IFollowUpPageTexts {
  const texts = defaultsWithFallback(
    pageTranslation?.body,
    fallbackPageTranslation?.body
  );
  const commonFollowUpsTexts = defaultsWithFallback(
    commonTranslation?.followups,
    fallbackCommonTranslation.followups
  );
  const commonBreakdownsTexts = defaultsWithFallback(
    commonTranslation?.breakdowns,
    fallbackCommonTranslation.breakdowns
  );
  const title = prepareFollowUpTitle(followUp, {
    titleUpdateLabel: commonFollowUpsTexts.updateLabel,
    linkmailTypeAmendment: commonBreakdownsTexts.linkmailTypeAmendment,
    productionTypes,
  });

  return {
    title,
    sentBy: prepareSent(
      {
        senderInfoFormat: texts.senderInfoFormat,
        dateTimeFormat: commonBreakdownsTexts.dateTimeFormat,
      },
      followUp
    ),
    breakdownInfo: texts.followupInfoFormat.replace("{followup}", ""),
    breakdownTitle: followUp.productionTitle,
    pageTitle: getPageTitle(
      commonTranslation,
      PageTitleKey.FollowUp,
      (format) => format.replace("{followUpName}", title)
    ),
  };
}

export function prepareActionList(
  pageTranslation: IFollowUpPageTranslation
): IActionList {
  const items =
    pageTranslation?.header?.pageHeaderElements ||
    fallbackPageTranslation.header.pageHeaderElements;

  return {
    moreButtonText: "",
    items: items.map(
      (item): IActionListItem => ({
        variant: "primary",
        id: item.id,
        text: item.label,
        imageSourceUrl: item.icon,
      })
    ),
  };
}
